import { nativeXrplTokenAddress } from "../../core/constants";
import { TrustLineAsset } from "../../core/types/tokens";
import {
  TrustLine,
  XrplTokenBalance,
  XrplTxStatus,
} from "../../core/types/xrpl";
import { formatBNToReadable } from "../internal/numberService";

interface JsonRpcError {
  error: string;
}

interface JsonRpcResponse<T extends Record<string, any>> {
  result: T | JsonRpcError;
}

type AccountLinesRpcResponse = {
  account: string;
  lines: TrustLine[];
};

type TransactionStatusResponse = {
  validated: boolean;
  meta: {
    TransactionResult: string;
  };
};

type AccountInfoRpcResponse = {
  account_data: {
    /**
     * Balance in drops (1/1000000 XRP)
     */
    Balance: string;
    /**
     * Number of trust lines held by the account
     */
    OwnerCount: number;
  };
};

type ServerStateRpcResponse = {
  state: {
    validated_ledger: {
      reserve_base: string;
      reserve_inc: string;
    };
  };
};

export class XrplRpcClient {
  private rpcUrl: string;

  constructor(rpcUrl: string) {
    this.rpcUrl = rpcUrl;
  }

  async getBalance(address: string, tokenAddress: string): Promise<string> {
    if (tokenAddress.toLowerCase() === nativeXrplTokenAddress.toLowerCase()) {
      return this.getNativeBalance(address);
    }

    return this.getIssuedCurrencyBalance(address, tokenAddress);
  }

  async getAllBalances(address: string): Promise<XrplTokenBalance[]> {
    const [nativeBalance, trustLineBalances] = await Promise.all([
      this.getNativeBalance(address),
      this.getTrustLines(address),
    ]);

    return [
      {
        balance: nativeBalance,
        address: nativeXrplTokenAddress,
      },
      ...trustLineBalances.lines.map((line) => ({
        balance: line.balance,
        address: `${line.currency}.${line.account}`,
      })),
    ];
  }

  async getTrustLines(
    address: string,
    issuer?: string
  ): Promise<AccountLinesRpcResponse> {
    return this.call<AccountLinesRpcResponse>("account_lines", [
      {
        account: address,
        ledger_index: "validated",
        peer: issuer,
      },
    ]);
  }

  async getTrustLine(
    address: string,
    asset: TrustLineAsset
  ): Promise<TrustLine | null> {
    const response = await this.getTrustLines(address, asset.issuer);

    const trustLine = response.lines.find(
      (line) => line.currency === asset.code
    );

    return trustLine ?? null;
  }

  async accountActivatedInfo(address: string): Promise<{
    isActivated: boolean;
    reserveBaseBn: bigint;
  }> {
    const serverState = await this.getServerState();
    const reserveBaseBn = BigInt(
      serverState.state.validated_ledger.reserve_base
    );

    try {
      const accountInfo = await this.getAccountInfo(address);
      const balanceBn = BigInt(accountInfo.account_data.Balance);

      return {
        isActivated: balanceBn >= reserveBaseBn,
        reserveBaseBn,
      };
    } catch (error: any) {
      if (error.message?.includes("actNotFound")) {
        return { isActivated: false, reserveBaseBn };
      }

      throw error;
    }
  }

  /**
   * Waits for a transaction to be validated and returns its final status.
   * Resolves to 'success' or throws an error with the failed status.
   */
  async waitForTransaction(
    txHash: string,
    { interval = 2_000, timeout = 20_000 } = {}
  ): Promise<XrplTxStatus> {
    const startTime = Date.now();

    while (true) {
      try {
        const response = await this.call<TransactionStatusResponse>("tx", [
          {
            transaction: txHash,
            binary: false,
          },
        ]);

        if (!response.validated) {
          if (Date.now() - startTime > timeout) {
            throw new Error(
              `Transaction ${txHash} not validated within timeout`
            );
          }

          // eslint-disable-next-line @typescript-eslint/no-loop-func
          await new Promise((res) => setTimeout(res, interval));
          continue;
        }

        const status = response.meta?.TransactionResult;
        if (status === XrplTxStatus.SUCCESS) {
          return status;
        } else {
          throw new Error(`Transaction failed with status: ${status}`);
        }
      } catch (error: any) {
        // txnNotFound = still pending or non-existent
        if (error?.message?.includes("txnNotFound")) {
          if (Date.now() - startTime > timeout) {
            throw new Error(`Transaction ${txHash} not found within timeout`);
          }

          // eslint-disable-next-line @typescript-eslint/no-loop-func
          await new Promise((res) => setTimeout(res, interval));
          continue;
        }

        throw error;
      }
    }
  }

  private async getServerState(): Promise<ServerStateRpcResponse> {
    return this.call<ServerStateRpcResponse>("server_state", [{}]);
  }

  private async getAccountInfo(
    address: string
  ): Promise<AccountInfoRpcResponse> {
    return this.call<AccountInfoRpcResponse>("account_info", [
      {
        account: address,
        ledger_index: "validated",
      },
    ]);
  }

  /**
   * Returns the balance of the user in the native XRP token
   * formatted as a string
   */
  private async getNativeBalance(address: string): Promise<string> {
    const [accountInfo, serverState] = await Promise.all([
      this.getAccountInfo(address),
      this.getServerState(),
    ]);

    const balance = BigInt(accountInfo.account_data.Balance);
    const ownerCount = BigInt(accountInfo.account_data.OwnerCount);
    const reserveBase = BigInt(serverState.state.validated_ledger.reserve_base);
    const reserveIncrement = BigInt(
      serverState.state.validated_ledger.reserve_inc
    );

    const reserveBalance = reserveBase + ownerCount * reserveIncrement;

    const spendableBalance = balance - reserveBalance;

    return formatBNToReadable(spendableBalance, 6);
  }

  /**
   * Returns the balance of the user in the given issued currency (e.g. RLUSD)
   * formatted as a string
   */
  private async getIssuedCurrencyBalance(
    address: string,
    tokenAddress: string
  ): Promise<string> {
    const response = await this.getTrustLines(address);

    const tokenBalance = response.lines.find(
      (line) => `${line.currency}.${line.account}` === tokenAddress
    );

    return tokenBalance?.balance || "0";
  }

  private async call<T>(method: string, params: any[]): Promise<T> {
    const response = await fetch(this.rpcUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        jsonrpc: "2.0",
        id: 1,
        method,
        params,
      }),
    });

    const data: JsonRpcResponse<T extends Record<string, any> ? T : never> =
      await response.json();

    if (!data.result) {
      throw new Error(`Invalid response from RPC (${method})`);
    }

    if ("error" in data.result) {
      throw new Error(`Error from RPC (${method}): ${data.result.error}`);
    }

    return data.result;
  }
}
